import React, { useState, useEffect, useRef } from "react";

import { auth, provider, db } from "../firebase";

import {
  onAuthStateChanged,
  signInWithPopup,
} from "firebase/auth";

import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";

import VedAIFace from "../components/VedAIFace";

export default function FriendsAI() {
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState("");

  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey 👋 I’m VedAI. Talk to me…" },
  ]);

  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [mood, setMood] = useState("neutral");

  const [voiceLanguage, setVoiceLanguage] = useState("hi-IN");

  const audioRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const handleGoogleLogin = async () => {
  try {
    console.log("LOGIN STARTED");

    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    console.log("LOGIN SUCCESS:", user.uid);

    const userRef = doc(db, "vedai_users", user.uid);
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        name: user.displayName,
        email: user.email,
        plan: "FREE",
        createdAt: new Date(),
      });

      console.log("🔥 FIREBASE USER CREATED");
    }

  } catch (err) {
    console.log("❌ FIREBASE ERROR:", err);
  }
};

  // =========================
  // AUTH
  // =========================
  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  // =========================
  // AUTO SCROLL
  // =========================
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // =========================
  // PLAY AUDIO (SAFE)
  // =========================
  const playAudio = async (base64) => {
    try {
      if (!base64) return;

      const clean = base64.replace(/^data:audio\/mp3;base64,/, "");
      const byte = atob(clean);
      const arr = new Uint8Array(byte.length);

      for (let i = 0; i < byte.length; i++) {
        arr[i] = byte.charCodeAt(i);
      }

      const blob = new Blob([arr], { type: "audio/mp3" });
      const url = URL.createObjectURL(blob);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      const audio = new Audio(url);
      audioRef.current = audio;

      setIsTalking(true);

      audio.onended = () => {
        setIsTalking(false);
        URL.revokeObjectURL(url);
      };

      await audio.play();
    } catch (err) {
      console.log("Audio error:", err);
      setIsTalking(false);
    }
  };

  // =========================
  // SEND MESSAGE
  // =========================
  const sendMessage = async () => {
    const text = message.trim();
    if (!text || !user) return;

    const updated = [
      ...messages,
      { role: "user", content: text },
    ];

    setMessages(updated);
    setMessage("");
    setLoading(true);

    try {
      const res = await fetch("/api/friend-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.uid,
          message: text,
          gender: "female",
          plan: "free"   // 👈 ADD THIS
        }),
      });

      const data = await res.json();

      if (!data.success) {
        setMessages((p) => [
          ...p,
          { role: "assistant", content: data.error },
        ]);
        setLoading(false);
        return;
      }

      setMessages((p) => [
        ...p,
        { role: "assistant", content: data.reply },
      ]);

      setMood(data.mood || "neutral");

      await playAudio(data.audio);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  // =========================
  // VOICE INPUT (RESTORED)
  // =========================
  const startVoice = () => {
    const SR =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SR) {
      alert("Voice not supported in this browser");
      return;
    }

    const recognition = new SR();
    recognition.lang = voiceLanguage;

    setListening(true);

    recognition.onresult = (e) => {
      setMessage(e.results[0][0].transcript);
    };

    recognition.onend = () => setListening(false);

    recognition.start();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>🤖 VedAI Video Mode</div>

      <VedAIFace isTalking={isTalking} mood={mood} />

      {/* CHAT */}
      <div style={styles.chat}>
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              ...styles.msg,
              alignSelf:
                m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            {m.content}
          </div>
        ))}

        {loading && <div>VedAI is thinking...</div>}
        <div ref={endRef} />
      </div>

      {/* INPUT BAR (FIXED CLEAN UI) */}
      <div style={styles.inputBox}>
        <input
          ref={inputRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Talk to VedAI..."
          style={styles.input}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />

        <select
  value={voiceLanguage}
  onChange={(e) => setVoiceLanguage(e.target.value)}
>
  <option value="en-US">English</option>
  <option value="hi-IN">Hindi</option>
  <option value="gu-IN">Gujarati</option>
  <option value="mr-IN">Marathi</option>
  <option value="pa-IN">Punjabi</option>
  <option value="bn-IN">Bengali</option>
  <option value="ta-IN">Tamil</option>
  <option value="te-IN">Telugu</option>
  <option value="kn-IN">Kannada</option>
  <option value="ml-IN">Malayalam</option>
</select>

<button onClick={startVoice}>
  {listening ? "🎤..." : "🎤"}
</button>

<button onClick={sendMessage}>Send</button>
      </div>
    </div>
  );
}

// =========================
// STYLES (UNCHANGED BUT CLEANED)
// =========================
const styles = {
  container: {
    height: "100vh",
    background: "#0b1220",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
  },

  header: {
    padding: 15,
    fontSize: 18,
    borderBottom: "1px solid #222",
  },

  chat: {
    flex: 1,
    overflowY: "auto",
    padding: 10,
  },

  msg: {
    background: "#1f2937",
    margin: 5,
    padding: 10,
    borderRadius: 10,
    maxWidth: "70%",
  },

  inputBox: {
    display: "flex",
    padding: 10,
    gap: 10,
    alignItems: "center",
  },

  input: {
    flex: 1,
    padding: 10,
    background: "#111827",
    color: "#fff",
    border: "none",
    outline: "none",
    borderRadius: 6,
  },
};